import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import DashboardAnalytics from './DashboardAnalytics';
import DashboardProfile from './DashboardProfile';
import DashboardHistory from './DashboardHistory';
import DashboardUsers from './DashboardUsers';
import DashboardBusinessUsers from './DashboardBusinessUsers';
import DashboardHoneyPot from './DashboardHoneyPot';
import { API_BASE } from '../utils/apiBase';

export default function Dashboard({ 
  userRole, 
  subscription, 
  token, 
  userEmail,
  userProfile: initialUserProfile 
}) {
  const [activeTab, setActiveTab] = useState('analytics');
  const [qrHistory, setQrHistory] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [userProfile, setUserProfile] = useState(initialUserProfile || {});
  const isSuperAdmin = !!userProfile?.isSuperAdmin;

  // Extrair ID do usuário logado
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.id);
        console.log('[Dashboard] Current user ID:', payload.id);
      } catch (err) {
        console.error('Erro ao decodificar token:', err);
      }
    }
  }, [token]);

  // Sincronizar userProfile quando props mudam
  useEffect(() => {
    if (initialUserProfile) {
      console.log('[Dashboard] Sincronizando userProfile:', initialUserProfile);
      setUserProfile(initialUserProfile);
    }
  }, [initialUserProfile]);

  // Determinar quais abas o usuário pode acessar
  const getAvailableTabs = () => {
    const tabs = [];

    // Analytics - disponível para PRO, BUSINESS e ADMIN
    if (subscription === 'pro' || subscription === 'business' || userRole === 'admin') {
      tabs.push({ id: 'analytics', label: 'Dashboard', icon: '📊' });
    }

    // Perfil - disponível para todos
    tabs.push({ id: 'profile', label: 'Meu Perfil', icon: '👤' });

    // Histórico - disponível para todos
    tabs.push({ id: 'history', label: 'Histórico QR Codes', icon: '📋' });

    // Gerenciar Sub-usuários - apenas BUSINESS
    if (subscription === 'business') {
      tabs.push({ id: 'business-users', label: 'Meus Usuários', icon: '👥' });
    }

    // Todos os Usuários - apenas ADMIN
    if (userRole === 'admin') {
      tabs.push({ id: 'all-users', label: 'Todos os Usuários', icon: '🔧' });
    }

    if (isSuperAdmin) {
      tabs.push({ id: 'honeypot', label: 'HoneyPot', icon: '🛡️' });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // Fetch inicial de dados ao montar componente
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      console.log('[Dashboard] Carregando dados iniciais...');
      
      try {
        // Sempre buscar perfil do usuário
        await fetchUserProfile();

        // Sempre buscar histórico de QR Codes (disponível para todos)
        await fetchQRHistory();

        // Buscar usuários se for ADMIN
        if (userRole === 'admin') {
          await fetchAllUsers();
        }

        // Buscar sub-usuários se for BUSINESS
        if (subscription === 'business') {
          await fetchBusinessUsers();
        }

        console.log('[Dashboard] Dados iniciais carregados com sucesso');
        setInitialLoadComplete(true);
      } catch (err) {
        console.error('[Dashboard] Erro ao carregar dados iniciais:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!initialLoadComplete) {
      loadInitialData();
    }
  }, [token, userRole, subscription]);

  // Buscar histórico de QR Codes quando necessário
  useEffect(() => {
    if (activeTab === 'history' && qrHistory.length === 0 && initialLoadComplete) {
      fetchQRHistory();
    }
  }, [activeTab]);

  // Buscar usuários quando necessário (para ADMIN)
  useEffect(() => {
    if (activeTab === 'all-users' && userRole === 'admin' && allUsers.length === 0 && initialLoadComplete) {
      fetchAllUsers();
    }
  }, [activeTab]);

  // Buscar sub-usuários quando necessário (para BUSINESS)
  useEffect(() => {
    if (activeTab === 'business-users' && subscription === 'business' && businessUsers.length === 0 && initialLoadComplete) {
      fetchBusinessUsers();
    }
  }, [activeTab]);

  const fetchQRHistory = async () => {
    console.log('[Dashboard] Buscando histórico de QR Codes...');
    try {
      const res = await fetch(`${API_BASE}/api/qr/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('[Dashboard] QR History recebido:', data?.length || 0, 'items');
      setQrHistory(data || []);
    } catch (err) {
      console.error('[Dashboard] Erro ao buscar histórico:', err);
      setQrHistory([]);
    }
  };

  const fetchAllUsers = async () => {
    console.log('[Dashboard] Buscando todos os usuários...');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('[Dashboard] Usuários recebidos:', data?.length || 0, 'users');
      setAllUsers(data || []);
    } catch (err) {
      console.error('[Dashboard] Erro ao buscar usuários:', err);
      setAllUsers([]);
    }
  };

  const fetchBusinessUsers = async () => {
    console.log('[Dashboard] Buscando sub-usuários...');
    try {
      const res = await fetch(`${API_BASE}/api/business/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('[Dashboard] Sub-usuários recebidos:', data?.length || 0, 'users');
      setBusinessUsers(data || []);
    } catch (err) {
      console.error('[Dashboard] Erro ao buscar sub-usuários:', err);
      setBusinessUsers([]);
    }
  };

  const fetchUserProfile = async () => {
    console.log('[Dashboard] Buscando perfil do usuário...');
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('[Dashboard] Perfil do usuário recebido:', data);
      setUserProfile({
        name: data.name || '',
        phone: data.phone || '',
        company: data.company || '',
        acceptsPromos: data.acceptsPromos || false,
        email: data.email || userEmail,
        subscription: data.subscription || 'none',
        createdAt: data.createdAt || null
        ,isSuperAdmin: !!data.isSuperAdmin
      });
    } catch (err) {
      console.error('[Dashboard] Erro ao buscar perfil:', err);
    }
  };

  // Definir primeira aba disponível como padrão
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [subscription, userRole]);

  // Debug: Log do estado atual
  useEffect(() => {
    console.log('[Dashboard] Estado atual:', {
      activeTab,
      qrHistoryCount: qrHistory.length,
      allUsersCount: allUsers.length,
      businessUsersCount: businessUsers.length,
      userProfile,
      loading,
      initialLoadComplete
    });
  }, [activeTab, qrHistory, allUsers, businessUsers, userProfile, loading, initialLoadComplete]);

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-container">
        {/* Header com informações do usuário */}
        <div className="dashboard-header">
          <div className="dashboard-header-content">
            <div className="dashboard-title-section">
              <h1>Painel de Controle</h1>
              <p className="dashboard-subtitle">
                Bem-vindo, {userProfile?.name || userEmail}
              </p>
            </div>
            <div className="dashboard-user-badge">
              <div className={`badge badge-${subscription || 'free'}`}>
                {subscription === 'pro' && '⭐ PRO'}
                {subscription === 'business' && '💼 BUSINESS'}
                {userRole === 'admin' && '👑 ADMIN'}
                {!subscription && subscription !== 'pro' && subscription !== 'business' && userRole !== 'admin' && '🆓 FREE'}
              </div>
            </div>
          </div>
        </div>

        {/* Navegação por abas */}
        <div className="dashboard-tabs">
          {availableTabs.map(tab => (
            <button
              key={tab.id}
              className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Conteúdo da aba ativa */}
        <div className="dashboard-content">
          {loading && !initialLoadComplete ? (
            <div className="dashboard-loading">
              <div className="spinner-large"></div>
              <p>Carregando dados iniciais...</p>
            </div>
          ) : (
            <>
              {activeTab === 'analytics' && (
                <DashboardAnalytics 
                  userRole={userRole}
                  subscription={subscription}
                  token={token}
                  qrHistory={qrHistory}
                />
              )}

              {activeTab === 'profile' && (
                <DashboardProfile 
                  userProfile={userProfile}
                  token={token}
                  userEmail={userEmail}
                  onProfileUpdate={fetchUserProfile}
                />
              )}

              {activeTab === 'history' && (
                <DashboardHistory 
                  qrHistory={qrHistory}
                  token={token}
                  onRefresh={fetchQRHistory}
                />
              )}

              {activeTab === 'business-users' && (
                <DashboardBusinessUsers 
                  businessUsers={businessUsers}
                  token={token}
                  onRefresh={fetchBusinessUsers}
                />
              )}

              {activeTab === 'all-users' && (
                <DashboardUsers 
                  users={allUsers}
                  token={token}
                  currentUserId={currentUserId}
                  onRefresh={fetchAllUsers}
                />
              )}

              {activeTab === 'honeypot' && isSuperAdmin && (
                <DashboardHoneyPot token={token} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
